import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import logo from '../assets/icons/emis-logo.svg';
import profileImg from '../assets/icons/profile.svg';
import menuImg from '../assets/icons/menu.svg';
import menuToggleImg from '../assets/icons/menu-toggle.png';

import { AppDispatch, RootState } from '../store';
import { clearSession } from '../store/slices/sessionSlice';

/**
 * Top navbar, migrated from Angular's NavbarComponent top-navbar section.
 * Uses a plain dropdown instead of PrimeReact's <Menu> to avoid the
 * `hideOverlaysOnDocumentScrolling` context crash on scroll.
 */

const toggleMenuBar = () =>
  document.querySelector('.menu-header')?.classList.toggle('header-toggled');

const ANGULAR_APP_URL = 'http://localhost:4200';

export interface HeaderProps {
  onToggleMenu?: () => void;
}

const Header = ({ onToggleMenu }: HeaderProps = {}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const userName = useSelector((state: RootState) => state.session.username);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cross-domain logout iframe
  useEffect(() => {
    const iframe = document.createElement('iframe');
    iframe.src = `${ANGULAR_APP_URL}/assets/receiver.html`;
    iframe.style.display = 'none';
    iframe.onload = () => {
      iframeRef.current = iframe;
    };
    document.body.appendChild(iframe);
    return () => {
      iframeRef.current = null;
      document.body.removeChild(iframe);
    };
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    dispatch(clearSession());
    // Keep the app-shell auth guard (AppLayout) in sync with the token clear.
    window.dispatchEvent(new Event('auth:logout'));
    const iframe = iframeRef.current;
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        'COMMAND_CLEAR_STORAGE',
        new URL(ANGULAR_APP_URL).origin
      );
    }
    navigate('/');
  };

  const handleResetPassword = () => navigate('/reset-password');
  const handleToggleMenu = onToggleMenu ?? toggleMenuBar;

  return (
    <header className="app-header horizontal-menu" id="printPageButton">
      <nav className="navbar top-navbar">
        <div className="container">
          <div className="navbar-content">
            {/* Logo */}
            <div className="row">
              <img
                className="wd-100 ht-100 my-1"
                src={logo}
                alt="School Education Department"
              />
            </div>

            <ul className="navbar-nav">
              <li className="d-none d-sm-block nav-item">
                <img className="nav-img" src={menuImg} alt="" />
              </li>

              <li className="d-none d-sm-block nav-item profile">
                <span>{userName}</span>
              </li>

              {/* Profile dropdown — no PrimeReact dependency */}
              <li className="nav-item nav-profile" ref={dropdownRef as any}>
                <button
                  type="button"
                  className="nav-link"
                  aria-haspopup="true"
                  aria-expanded={dropdownOpen}
                  aria-label="Profile menu"
                  onClick={() => setDropdownOpen((prev) => !prev)}
                >
                  <img className="image-style" src={profileImg} alt="" />
                </button>

                {dropdownOpen && (
                  <ul className="profile-dropdown">
                    <li>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                          setDropdownOpen(false);
                          handleResetPassword();
                        }}
                      >
                        <i className="pi pi-key me-2" />
                        <span>Reset Password</span>
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                          setDropdownOpen(false);
                          handleLogout();
                        }}
                      >
                        <i className="pi pi-sign-out me-2" />
                        <span>Log Out</span>
                      </button>
                    </li>
                  </ul>
                )}
              </li>
            </ul>

            <button
              className="navbar-toggler navbar-toggler-right d-lg-none align-self-center"
              type="button"
              aria-label="Toggle navigation menu"
              onClick={handleToggleMenu}
            >
              <img height={24} width={24} src={menuToggleImg} alt="" />
            </button>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
